import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, UserCheck, Building2, ClipboardList, XCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { practitionerService } from '@/services/practitionerService';
import AddPractitionerForm from '@/components/forms/AddPractitionerForm';
import { getDEAValidationError } from '@/lib/validators';
import { CreatePractitionerRequest } from '@/services/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const practitionerOnboardingSchema = z.object({
  first_name: z
    .string()
    .trim()
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be at most 50 characters').nonempty('First name is required'),
  last_name: z
    .string()
    .trim()
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be at most 50 characters'),
  full_name: z
    .string()
    .trim()
    .optional()
    .or(z.literal('')),
  credentials: z.string().trim().min(2, 'Credential is required').optional().or(z.literal('')),
  email: z.string().trim().email('Please enter a valid email address').min(1, 'Email is required'),
  license_number: z
    .string()
    .trim()
    .max(30, 'License number must be at most 30 characters')
    .optional()
    .or(z.literal('')),
  npi_number: z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'NPI number must be 10 digits'),
  dea_number: z
    .string()
    .trim()
    .optional()
    .refine(
      value => {
        if (!value || value === '') return true;
        const error = getDEAValidationError(value);
        return error === null;
      },
      value => ({
        message: getDEAValidationError(value || '') || 'Invalid DEA number',
      })
    ),
  fax: z.string().trim().max(20, 'Fax must be at most 20 characters').optional().or(z.literal('')),
  phone: z
    .string()
    .trim()
    .max(20, 'Phone must be at most 20 characters')
    .optional()
    .or(z.literal('')),
  address_line_1: z
    .string()
    .trim()
    .min(5, 'Please enter a valid address')
    .max(100, 'Address must be at most 100 characters').nonempty('Address is required'),
  address_line_2: z
    .string()
    .trim()
    .max(100, 'Address line 2 must be at most 100 characters')
    .optional()
    .or(z.literal('')),
  city: z
    .string()
    .trim()
    .min(2, 'Please enter a valid city')
    .max(50, 'City must be at most 50 characters').nonempty('City is required'),
  state: z.string().trim().length(2, 'Please select a state').nonempty('State is required'),
  postal_code: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'Postal code must be 5 digits').nonempty('Postal code is required'),
});

type PractitionerOnboardingFormData = z.infer<typeof practitionerOnboardingSchema>;

const PractitionerOnboarding: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState('');
  const [clinicInfo, setClinicInfo] = useState<any>(null);
  const [npiValidation, setNpiValidation] = useState<{
    isValidating: boolean;
    exists: boolean;
    practitioner?: { id: string; name: string; npi: string };
  }>({ isValidating: false, exists: false });
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [duplicatePractitioner, setDuplicatePractitioner] = useState<{
    id: string;
    name: string;
    npi: string;
  } | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  const token = searchParams.get('token');
  const emailParam = searchParams.get('email');
  const firstNameParam = searchParams.get('first_name') || '';
  const lastNameParam = searchParams.get('last_name') || '';
  const initialFullName = `${firstNameParam} ${lastNameParam}`.trim();

  const form = useForm<PractitionerOnboardingFormData>({
    resolver: zodResolver(practitionerOnboardingSchema),
    defaultValues: {
      first_name: firstNameParam,
      last_name: lastNameParam,
      full_name: initialFullName,
      email: emailParam || '',
      credentials: '',
      license_number: '',
      npi_number: '',
      dea_number: '',
      fax: '',
      phone: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      postal_code: '',
    },
  });

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidationError('No invitation token provided');
        setIsValidating(false);
        return;
      }

      try {
        const response = await practitionerService.validateOnboardingInvite(token);
        if (response.success) {
          const { email, clinic, first_name, last_name } = response.data;
          setClinicInfo(clinic);
          form.setValue('email', email);
          // Fall back to names from the server if they weren't in the URL
          if (first_name && !form.getValues('first_name')) {
            form.setValue('first_name', first_name);
          }
          if (last_name && !form.getValues('last_name')) {
            form.setValue('last_name', last_name);
          }
          setIsValidating(false);
        }
      } catch (error: any) {
        console.error('Token validation failed:', error);
        const errorMessage = error?.response?.data?.error?.message || 'Invalid or expired invitation token';
        setValidationError(errorMessage);
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // Auto-update full_name when first_name or last_name changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'first_name' || name === 'last_name') {
        const firstName = value.first_name || '';
        const lastName = value.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        form.setValue('full_name', fullName);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Debounced NPI validation (public endpoint for unauthenticated onboarding)
  const validateNpi = useCallback(
    async (npi: string) => {
      if (!npi || npi.length !== 10 || !/^\d{10}$/.test(npi)) {
        setNpiValidation({ isValidating: false, exists: false });
        setDuplicatePractitioner(null);
        setLinkDialogOpen(false);
        return;
      }

      setNpiValidation(prev => ({ ...prev, isValidating: true }));

      try {
        const result = await practitionerService.checkPractitionerNpiExistsPublic(npi);
        const data = result?.data ?? result;
        setNpiValidation({
          isValidating: false,
          exists: data?.exists ?? false,
          practitioner: data?.practitioner,
        });

        if (data?.exists && data?.practitioner) {
          form.setError('npi_number', {
            type: 'manual',
            message: `A practitioner with NPI ${npi} already exists: ${data.practitioner?.name}`,
          });
          setDuplicatePractitioner({
            id: data.practitioner.id,
            name: data.practitioner.name || '',
            npi: data.practitioner.npi || npi,
          });
          setLinkDialogOpen(true);
        } else {
          form.clearErrors('npi_number');
          setDuplicatePractitioner(null);
          setLinkDialogOpen(false);
        }
      } catch (error) {
        console.error('NPI validation error:', error);
        setNpiValidation({ isValidating: false, exists: false });
        setDuplicatePractitioner(null);
        setLinkDialogOpen(false);
      }
    },
    [form]
  );

  useEffect(() => {
    const npi = form.watch('npi_number');
    const timeoutId = setTimeout(() => {
      if (npi && npi.length === 10) {
        validateNpi(npi);
      } else {
        setNpiValidation({ isValidating: false, exists: false });
        setDuplicatePractitioner(null);
        setLinkDialogOpen(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [form.watch('npi_number'), validateNpi]);

  const handleLinkExistingPractitioner = async () => {
    if (!duplicatePractitioner || !token) return;
    setIsLinking(true);
    try {
      await practitionerService.submitOnboarding(token, {
        link_existing_practitioner_id: duplicatePractitioner.id,
      } as CreatePractitionerRequest & { link_existing_practitioner_id: string });

      toast({
        title: 'Success',
        description: 'Your existing practitioner account has been linked to this clinic successfully!',
        variant: 'success',
      });

      setLinkDialogOpen(false);
      setDuplicatePractitioner(null);
      form.clearErrors('npi_number');
      setNpiValidation({ isValidating: false, exists: false });
      navigate('/practitioner-onboarding/success');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.message || error?.message || 'Failed to link practitioner.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLinking(false);
    }
  };

  const handleSubmit = async (data: PractitionerOnboardingFormData) => {
    if (!token) {
      toast({
        title: 'Error',
        description: 'Invalid invitation token',
        variant: 'destructive',
      });
      return;
    }

    // If NPI exists, show link dialog instead of creating new
    if (npiValidation.exists && duplicatePractitioner) {
      setLinkDialogOpen(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData: CreatePractitionerRequest = {
        first_name: data.first_name,
        last_name: data.last_name,
        full_name: data.full_name || `${data.first_name} ${data.last_name}`.trim(),
        credentials: data.credentials || undefined,
        email: data.email,
        npi_number: data.npi_number,
        clinic_id: clinicInfo?.id,
        address_line_1: data.address_line_1,
        city: data.city,
        state: data.state,
        postal_code: data.postal_code,
        license_number: data.license_number || undefined,
        dea_number: data.dea_number || undefined,
        fax: data.fax || undefined,
        phone: data.phone || undefined,
        address_line_2: data.address_line_2 || undefined,
      };

      await practitionerService.submitOnboarding(token, requestData);

      toast({
        title: 'Success',
        description: 'Your onboarding request has been submitted successfully!',
        variant: 'success',
      });

      navigate('/practitioner-onboarding/success');
    } catch (error: any) {
      console.error('Error submitting onboarding:', error);

      const errorMessage = error?.response?.data?.error?.message || error?.message || 'Failed to submit onboarding request';

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while validating
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-teal/5 via-white to-brand-teal/10 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-40"></div>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10"
        >
          <Card className="w-full max-w-md rounded-2xl border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardContent className="pt-8 pb-8">
              <div className="flex flex-col items-center justify-center space-y-4">
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-brand-teal to-[#2D6A78] rounded-2xl flex items-center justify-center shadow-lg"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <Loader2 className="h-8 w-8 text-white" />
                </motion.div>
                <p className="text-gray-600 font-medium text-center">Validating your invitation...</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Show error if validation failed
  if (validationError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-40"></div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10"
        >
          <Card className="w-full max-w-md rounded-2xl border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center pb-2">
              <motion.div 
                className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <XCircle className="h-8 w-8 text-white" />
              </motion.div>
              <CardTitle className="text-xl text-gray-900">Invalid Invitation</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-6">{validationError}</p>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full h-12 rounded-xl bg-brand-teal hover:bg-brand-teal/90 text-white shadow-lg shadow-brand-teal/20"
                >
                  Go to Login
                </Button>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-teal/5 via-white to-brand-teal/10 py-12 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-40"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-brand-teal/20 to-brand-teal/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-brand-teal/10 to-brand-teal/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="shadow-xl rounded-2xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-brand-teal to-[#2D6A78] text-white p-8">
              <div className="flex items-center space-x-4">
                <motion.div 
                  className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center"
                  whileHover={{ scale: 1.05, rotate: 5 }}
                >
                  <UserCheck className="h-8 w-8 text-white" />
                </motion.div>
                <div>
                  <CardTitle className="text-2xl font-bold">Practitioner Onboarding</CardTitle>
                  <p className="text-white/70 mt-1">
                    Complete your profile to join {clinicInfo?.name || 'the clinic'}
                  </p>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-8">
              {clinicInfo && (
                <motion.div 
                  className="bg-gradient-to-r from-brand-teal/5 to-brand-teal/10 border border-brand-teal/20 rounded-2xl p-5 mb-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-brand-teal to-[#2D6A78] rounded-xl flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Clinic Information</h3>
                  </div>
                  <p className="text-gray-700 text-sm ml-13">
                    <strong>Name:</strong> {clinicInfo.name}
                  </p>
                  {clinicInfo.city && clinicInfo.state && (
                    <p className="text-gray-700 text-sm ml-13">
                      <strong>Location:</strong> {clinicInfo.city}, {clinicInfo.state}
                    </p>
                  )}
                </motion.div>
              )}

              <motion.div 
                className="bg-gradient-to-r from-brand-teal/5 to-brand-teal/10 border border-brand-teal/20 rounded-2xl p-5 mb-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-brand-teal to-[#2D6A78] rounded-xl flex items-center justify-center">
                    <ClipboardList className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-900">What to Expect</h4>
                </div>
                <ol className="text-gray-700 text-sm space-y-2 list-decimal list-inside ml-2">
                  <li>Complete the form below with your practitioner information</li>
                  <li>Submit your application for admin review</li>
                  <li>Admin will approve your request</li>
                  <li>Your account is activated immediately on approval</li>
                  <li>You'll receive an email with your portal access credentials</li>
                </ol>
              </motion.div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                  <AddPractitionerForm form={form} onSubmit={handleSubmit} isEdit={false} />

                  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-100">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/login')}
                      disabled={isSubmitting}
                      className="rounded-xl h-11 px-6"
                    >
                      Cancel
                    </Button>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        type="submit"
                        disabled={isSubmitting || npiValidation.isValidating}
                        className="h-11 px-8 rounded-xl bg-brand-teal hover:bg-brand-teal/90 text-white shadow-lg shadow-brand-teal/20 font-semibold"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          'Submit Application'
                        )}
                      </Button>
                    </motion.div>
                  </div>
                </form>
              </Form>

              <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Link existing practitioner</DialogTitle>
                    <DialogDescription className="pt-4">
                      A practitioner named{' '}
                      <span className="font-semibold">{duplicatePractitioner?.name}</span> with NPI{' '}
                      <span className="font-semibold">{duplicatePractitioner?.npi}</span> already
                      exists in the system. Would you like to link this practitioner to the clinic
                      instead of creating a new account?
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="flex-row justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setLinkDialogOpen(false);
                      }}
                      disabled={isLinking}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      className="h-11 rounded-xl bg-brand-teal hover:bg-brand-teal/90 text-white"
                      onClick={handleLinkExistingPractitioner}
                      disabled={isLinking}
                    >
                      {isLinking ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Linking...
                        </>
                      ) : (
                        'Link Practitioner'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PractitionerOnboarding;
